import {
  BeakerIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { CalendarDaysIcon, UserIcon } from "@heroicons/react/24/solid";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { OverlayLoading } from "@via/components/loading/loading";
import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import { useGetDataSourceList } from "@via/pages/data/data-connections/data-connections.api.tsx";
// import { ActionButton } from "@via/components/action-button/action-button.tsx";
import { usePipelineRevisionHistory } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import ProjectsFilter from "@via/pages/projects/projects-filter.tsx";
import {
  useCreateProject,
  useProjectList,
} from "@via/pages/projects/projects.api.tsx";
import {
  useChangeProject,
  useDuplicateRun,
  useGetRunDuplicationStatus,
  useGetDuplicateCheck,
  useGetNextProjectPipelineId,
  useCancelRunDuplication,
} from "@via/pages/projects/runs/runs.api.tsx";
import RunPermissionsSection from "@via/pages/projects/runs/run-permissions-section";
import {
  ModalSize,
  RUN_MANAGEMENT_MODALS,
  // BUTTONS,
  // ROUTES,
} from "@via/utils/constants";
import { requiredValidation } from "@via/utils/form-validators";
import {
  formatDateString,
  normalizeProviderPath,
  classNames,
} from "@via/utils/helpers";
import { SelectOption } from "@via/utils/interfaces";
import {
  DataSourceProvider,
  DuplicateRunRequestBody,
  ListProjectsResponseBodyItem,
  PermissionType,
  ProjectViewType,
  DuplicationItemStatus,
  DuplicationStatus,
} from "common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";
import { useNavigate } from "react-router-dom";
import DeleteModal from "@via/components/delete-modal/delete-modal";

interface ChangeProjectDuplicateRunModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  mode: (typeof RUN_MANAGEMENT_MODALS)[keyof typeof RUN_MANAGEMENT_MODALS];
  pipelineId?: string;
  runId: string;
  projectId?: number;
}

type DuplicateRunFormValues = {
  projectId: number;
  runId: string;
  // API shape
  include?: {
    logs?: boolean;
    reports?: {
      dataSourceId: number;
      path: string;
    };
  };
  // UI-only toggle for rendering 'reports' section
  includeReportsToggle?: boolean;
  // UI-only toggle for rendering 'dataSets' section
  includeDataSetsToggle?: boolean;
  // UI-only toggle for changing pipeline version
  changePipelineVersion?: boolean;
  // UI-only toggle for inheriting permissions from project
  inheritFromProject?: boolean;
};

type ChangeProjectFormValues = {
  pipelineId: number;
  projectId: number;
  runId: string;
  inheritFromProject?: boolean;
};

const defaultParams = {
  sort: [{ id: "updatedAt", desc: true }],
  filters: [{ id: "type", value: ProjectViewType.MyProjects }],
};

// Helper function to get status color class
const getStatusColorClass = (status: DuplicationItemStatus): string => {
  switch (status) {
    case DuplicationItemStatus.ItemOk:
      return "text-green-600";
    case DuplicationItemStatus.ItemInProgress:
      return "text-blue-600";
    case DuplicationItemStatus.ItemPending:
      return "text-gray-500";
    case DuplicationItemStatus.ItemFailed:
      return "text-red-600";
    default:
      return "text-gray-600";
  }
};

const ChangeProjectDuplicateRunModal = ({
  open,
  setOpen,
  mode,
  pipelineId,
  runId,
  projectId,
}: ChangeProjectDuplicateRunModalProps) => {
  const navigate = useNavigate();
  const {
    mutate: createProject,
    data: createProjectData,
    isPending: isCreateProjectLoading,
    isSuccess: isCreateProjectSuccess,
  } = useCreateProject();
  const {
    data: pipelineRevisionHistoryData,
    isLoading: isPipelineRevisionHistoryLoading,
    isSuccess: isPipelineRevisionHistorySuccess,
  } = usePipelineRevisionHistory(pipelineId ?? "");
  const {
    mutateAsync: duplicateRun,
    isPending: isDuplicateRunLoading,
    isSuccess: isDuplicateRunSuccess,
    data: duplicateRunData,
  } = useDuplicateRun();
  const { mutateAsync: changeProject, isPending: isChangeProjectModalLoading } =
    useChangeProject();

  const { mutate: cancelDuplication, isPending: isCancellingDuplication } =
    useCancelRunDuplication();

  const { data: dataSourceList, isLoading: isDataSourceListLoading } =
    useGetDataSourceList();

  // Handle cancellation
  const handleCancelDuplication = () => {
    setIsRunDuplicationToBeCanceled(true);
  };

  // Check for existing duplication status
  // First, check if we should poll (before using duplicationStatus)
  const shouldPollStatusBase =
    open && mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN;

  // Check if reports and datasets can be duplicated (GCS to GCS only)
  const { data: duplicateCheckData, isLoading: isDuplicateCheckLoading } =
    useGetDuplicateCheck(
      runId,
      open && mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN
    );

  const canDuplicateReports = duplicateCheckData?.canDuplicateReports ?? true;
  const canDuplicateDatasets = duplicateCheckData?.canDuplicateDatasets ?? true;

  const {
    data: duplicationStatus,
    refetch: refetchDuplicationStatus,
    isLoading: isDuplicationStatusLoading,
  } = useGetRunDuplicationStatus(runId, shouldPollStatusBase);

  const shouldShowLoadingState =
    isDuplicateRunSuccess && !duplicationStatus && !isDuplicationStatusLoading;

  // Poll for status if duplicate was just initiated or if status is in progress
  // Note: This is used in useEffect for refetching
  const shouldPollStatus =
    shouldPollStatusBase &&
    (isDuplicateRunSuccess ||
      duplicationStatus?.status === DuplicationStatus.InProgress);

  // Update polling interval based on status
  // Poll when duplicate is initiated (even if status not yet available) or when status is in progress
  useEffect(() => {
    if (shouldPollStatus) {
      const status = duplicationStatus?.status;
      // Poll if status is in progress, or if duplicate was just initiated (status might not be available yet)
      if (
        status === DuplicationStatus.InProgress ||
        (isDuplicateRunSuccess && !status)
      ) {
        const interval = setInterval(() => {
          refetchDuplicationStatus();
        }, 5000); // Poll every 5 seconds
        return () => clearInterval(interval);
      }
    }
  }, [
    shouldPollStatus,
    duplicationStatus?.status,
    isDuplicateRunSuccess,
    refetchDuplicationStatus,
  ]);

  const [pipelineRevisions, setPipelineRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState<SelectOption | null>(
    null
  );
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);
  const [searchedInput, setSearchedInput] = useState("");
  const [cumulativeProjects, setCumulativeProjects] = useState<
    ListProjectsResponseBodyItem[]
  >([]);
  const [missingProjects, setMissingProjects] = useState<
    ListProjectsResponseBodyItem[]
  >([]);
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(defaultParams.filters);
  const [projectsPage, setProjectsPage] = useState(0);
  const projectOptionSize = 10;
  const [isRunDuplicationToBeCanceled, setIsRunDuplicationToBeCanceled] =
    useState<boolean>(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DuplicateRunFormValues | ChangeProjectFormValues>({
    mode: "onChange",
    defaultValues: {
      projectId,
      runId,
      inheritFromProject: true,
      ...(mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN
        ? {
            include: { logs: false },
            includeReportsToggle: false,
            includeDataSetsToggle: false,
            changePipelineVersion: false,
          }
        : {}),
    },
  });

  // Watchers for conditional UI + auto-fill behavior
  const includeReportsToggle = watch("includeReportsToggle") as
    | boolean
    | undefined;
  const includeDataSetsToggle = watch("includeDataSetsToggle") as
    | boolean
    | undefined;
  const changePipelineVersion = watch("changePipelineVersion") as
    | boolean
    | undefined;
  const selectedDataSourceId = watch(
    "include.reports.dataSourceId" as const
  ) as number | undefined;
  const selectedPath = watch("include.reports.path" as const) as
    | string
    | undefined;

  // Show destination/path selection if either Reports or Data Sets is selected
  const showDestinationSelection =
    includeReportsToggle || includeDataSetsToggle;

  useEffect(() => {
    if (
      !showAdditionalOptions &&
      (includeReportsToggle || includeDataSetsToggle)
    ) {
      setShowAdditionalOptions(true);
    }
  }, [includeReportsToggle, includeDataSetsToggle, showAdditionalOptions]);

  // Get next project pipeline ID for path prediction
  const { data: nextProjectPipelineIdData } = useGetNextProjectPipelineId(
    open &&
      mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN &&
      showDestinationSelection
  );

  // Calculate final paths for Reports and Data Sets
  const finalPaths = useMemo(() => {
    if (!selectedPath) return null;

    // Ensure path ends with / if it doesn't already
    const basePath = selectedPath.endsWith("/")
      ? selectedPath
      : `${selectedPath}/`;

    // Get next project pipeline ID or use a placeholder
    const runIdSuffix = nextProjectPipelineIdData?.nextId
      ? String(nextProjectPipelineIdData.nextId)
      : "run";

    const reportsPath = `${basePath}reports${runIdSuffix}`;
    const datasetsPath = `${basePath}reports${runIdSuffix}/datasets`;

    return {
      reports: reportsPath,
      datasets: datasetsPath,
    };
  }, [selectedPath, nextProjectPipelineIdData?.nextId]);

  // Build data source options
  const dataSourceOptions = useMemo(
    () =>
      (dataSourceList ?? []).map(ds => ({
        label: ds.name,
        value: ds.id,
        metadata: {
          path: ds.path,
          provider: ds.provider as DataSourceProvider,
        },
      })),
    [dataSourceList]
  );

  // Auto-fill the path when dataSource changes (for both Reports and Data Sets)
  useEffect(() => {
    if (!showDestinationSelection || !selectedDataSourceId) return;
    const found = dataSourceOptions.find(o => o.value === selectedDataSourceId);
    if (found?.metadata?.path) {
      const normalizedPath = normalizeProviderPath(
        found.metadata.provider,
        found.metadata.path
      );
      // Set path for both reports and dataSet (they share the same destination)
      setValue("include.reports.path", normalizedPath, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    showDestinationSelection,
    selectedDataSourceId,
    dataSourceOptions,
    setValue,
  ]);

  // Reset include values when changePipelineVersion toggle changes
  useEffect(() => {
    if (pipelineRevisions.length > 0 && changePipelineVersion) {
      // When changing pipeline version, reset logs and reports to false
      setValue("include.logs", false, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("includeReportsToggle", false, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [changePipelineVersion, pipelineRevisions.length, setValue]);

  // Reset toggles if duplication is not supported
  useEffect(() => {
    if (!isDuplicateCheckLoading && duplicateCheckData) {
      if (!canDuplicateReports) {
        setValue("includeReportsToggle", false, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("include.reports", undefined, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      if (!canDuplicateDatasets) {
        setValue("includeDataSetsToggle", false, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [
    canDuplicateReports,
    canDuplicateDatasets,
    isDuplicateCheckLoading,
    duplicateCheckData,
    setValue,
  ]);

  const { data: projects, isPending: isProjectsLoading } = useProjectList(
    projectsPage,
    projectOptionSize,
    defaultParams.sort,
    [...columnFilters, { id: "name", value: searchedInput }]
  );

  // Fetch missing projects separately
  const { data: missingProjectListData, isLoading: isMissingProjectLoading } =
    useProjectList(
      0,
      1,
      [], // no sort
      [
        { id: "type", value: columnFilters[0].value },
        { id: "id", value: projectId ?? "" },
      ],
      projectId ? true : false // only fetch if there is a missing project
    );

  useEffect(() => {
    if (missingProjectListData?.data) {
      setMissingProjects(missingProjectListData.data);
    }
  }, [missingProjectListData]);

  const fetchMoreProjects = useCallback(() => {
    setProjectsPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((projects?.total ?? 0) / projectOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [projects?.total, projectOptionSize]);

  useEffect(() => {
    setCumulativeProjects([]);
  }, [columnFilters]);

  useEffect(() => {
    if (!projects) return;

    setCumulativeProjects(prevProjects => {
      const newData = projects.data ?? [];

      if (!newData.length) return [];

      // Append new results while preventing duplicates
      const existingIds = new Set(prevProjects.map(p => p.id));
      const newProjects = newData.filter(p => !existingIds.has(p.id));

      return [...prevProjects, ...newProjects];
    });
  }, [projects?.data, projectsPage, searchedInput, columnFilters]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setProjectsPage(0);
    setSearchedInput(value);
  }, 300);

  useEffect(() => {
    if (pipelineRevisionHistoryData) {
      const options = pipelineRevisionHistoryData?.data.map(rev => {
        return {
          label: rev?.version.length
            ? `Version: ${rev.version}`
            : "Version: 1.0.0",
          version: rev?.version.length ? rev.version : "1.0.0",
          createdAt: rev.createdAt,
          revComment: rev.revComment.length
            ? rev.revComment
            : "Initial Version",
          value: rev.id,
        };
      });
      setPipelineRevisions(options);
      let selected = options?.find(
        (rev: SelectOption) => rev?.value === pipelineId
      );
      if (!selected) {
        selected = options[0];
      }
      setSelectedRevision(selected);
    }
  }, [
    pipelineRevisionHistoryData,
    pipelineId,
    isPipelineRevisionHistorySuccess,
  ]);

  // Don't close modal on duplicate success - keep it open to show status
  // Modal will be closed when user clicks "Go to Duplicated Run" button after completion

  useEffect(() => {
    if (createProjectData) {
      const { id } = createProjectData;
      setValue("projectId", id, { shouldValidate: true });
    }
  }, [isCreateProjectSuccess, createProjectData]);

  const handlePipelineRevisionChange = selected => {
    setSelectedRevision(selected);
  };

  const handleDuplicateRun = async (
    raw: DuplicateRunFormValues
  ): Promise<void> => {
    // Determine the pipeline revision ID to use
    const hasRevisions = pipelineRevisions.length > 0;
    const revisionValue =
      hasRevisions && raw.changePipelineVersion
        ? (selectedRevision?.value ?? pipelineId)
        : pipelineId;
    const coercedPipelineId = Number(revisionValue);

    if (isNaN(coercedPipelineId)) {
      // Optionally, show an error notification or handle this case gracefully
      console.error("Invalid pipeline revision ID for duplicate run.");
      return;
    }

    // Construct the payload for the API
    // Reports and Data Sets share the same destination and path
    const sharedDestination = raw.include?.reports;
    const includeReports = raw.includeReportsToggle && sharedDestination;
    const includeDataSets = raw.includeDataSetsToggle && sharedDestination;

    const payload = {
      projectId: raw.projectId,
      pipelineId: coercedPipelineId,
      runId,
      inheritFromProject: raw.inheritFromProject,
      include: {
        logs:
          hasRevisions && raw.changePipelineVersion
            ? false
            : !!raw.include?.logs,
        ...(hasRevisions && raw.changePipelineVersion
          ? {}
          : {
              ...(includeReports ? { reports: sharedDestination } : {}),
              ...(includeDataSets ? { dataSet: sharedDestination } : {}),
            }),
      },
    } as DuplicateRunRequestBody & { runId: string };

    await duplicateRun(payload);
  };

  const handleCreateNewProject = (name: string): void => {
    createProject({
      name,
      permission: PermissionType.UserOwned,
      summary: "",
    });
  };

  // Resolves true only when a project change succeeds, which is the one outcome that closes
  // the dialog. An invalid form and a started duplication both resolve false: the duplication
  // keeps the dialog open on purpose, to show its status. A failed request rejects, which also
  // keeps it open.
  const handleConfirmModal = async (): Promise<boolean> => {
    let changedProject = false;
    await handleSubmit(async data => {
      if (mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN) {
        await handleDuplicateRun(data as DuplicateRunFormValues);
      } else {
        await changeProject(data as ChangeProjectFormValues);
        changedProject = true;
      }
    })();
    return changedProject;
  };

  const projectListOptions = useMemo(() => {
    const map = new Map<number, ListProjectsResponseBodyItem>();
    cumulativeProjects.forEach(project => {
      map.set(project.id, project);
    });
    missingProjects.forEach(project => {
      map.set(project.id, project);
    });
    const mergedProjects = Array.from(map.values());
    return (
      mergedProjects.map(({ name, id, owner, updatedAt }) => ({
        label: name,
        value: id,
        metadata: { name, owner, updatedAt },
      })) ?? []
    );
  }, [cumulativeProjects, missingProjects]);

  // Helper function to get status display text
  const getStatusText = (status: DuplicationItemStatus): string => {
    switch (status) {
      case DuplicationItemStatus.ItemOk:
        return "OK";
      case DuplicationItemStatus.ItemInProgress:
        return "In Progress";
      case DuplicationItemStatus.ItemPending:
        return "Pending";
      case DuplicationItemStatus.ItemFailed:
        return "Failed";
      default:
        return status;
    }
  };

  // Show status UI if duplication is in progress or was just initiated
  const showStatusUI =
    mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN &&
    ((duplicationStatus &&
      ![DuplicationStatus.Completed, DuplicationStatus.Cancelled].includes(
        duplicationStatus.status
      )) ||
      isDuplicateRunSuccess);

  // Check if duplication is completed
  const isDuplicationCompleted =
    duplicationStatus?.status === DuplicationStatus.Completed;

  // Check if there are any errors
  const hasErrors =
    duplicationStatus?.status === DuplicationStatus.Failed ||
    duplicationStatus?.errorMessage ||
    duplicationStatus?.reportsErrorMessage ||
    duplicationStatus?.datasets?.some(ds => ds.errorMessage) ||
    duplicationStatus?.reportsStatus === DuplicationItemStatus.ItemFailed;

  // Get duplicated run ID from response or status
  const duplicatedRunId = duplicateRunData?.duplicatedRunId;

  // Handle navigation to duplicated run
  const handleGoToDuplicatedRun = () => {
    if (duplicatedRunId) {
      navigate(`/runs/${duplicatedRunId}`);
      setOpen(false);
    }
  };

  // Auto-refetch status when duplicate is initiated
  useEffect(() => {
    if (
      isDuplicateRunSuccess &&
      open &&
      mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN
    ) {
      // Small delay to ensure backend has created the duplication job
      const timer = setTimeout(() => {
        refetchDuplicationStatus();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isDuplicateRunSuccess, open, mode, refetchDuplicationStatus]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={mode}
      size={ModalSize.Large}
      isConfirmLoading={isCreateProjectLoading || isDuplicateRunLoading}
      onConfirm={showStatusUI ? undefined : handleConfirmModal}
      confirmText={showStatusUI ? undefined : mode}>
      {showStatusUI ? (
        <div className="my-4 mx-0 max-w-[550px]">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0">
                {isDuplicationCompleted ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse"></div>
                )}
              </div>
              <p className="text-base font-semibold text-gray-900 ">
                {isDuplicationCompleted
                  ? "Duplication completed successfully."
                  : "Duplicating run and associated data... Processing time varies based on the size of reports and datasets."}
              </p>
            </div>

            {/* Status Items - Show loading state if status not yet available or if new duplication just started */}
            {shouldShowLoadingState ||
            (!duplicationStatus && isDuplicationStatusLoading) ? (
              <div className="ml-4 text-sm text-gray-600">
                Loading duplication status...
              </div>
            ) : duplicationStatus ? (
              <div className="space-y-5 ml-4">
                {/* Config Status */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">
                    Duplicate Config
                  </span>
                  <div className="flex items-center gap-2">
                    {duplicationStatus.configStatus ===
                      DuplicationItemStatus.ItemOk && (
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    )}
                    {duplicationStatus.configStatus ===
                      DuplicationItemStatus.ItemInProgress && (
                      <ClockIcon className="h-5 w-5 text-blue-600" />
                    )}
                    {duplicationStatus.configStatus ===
                      DuplicationItemStatus.ItemPending && (
                      <ClockIcon className="h-5 w-5 text-gray-500" />
                    )}
                    {duplicationStatus.configStatus ===
                      DuplicationItemStatus.ItemFailed && (
                      <XCircleIcon className="h-5 w-5 text-red-600" />
                    )}
                    <span
                      className={classNames(
                        "text-sm font-medium",
                        getStatusColorClass(duplicationStatus.configStatus)
                      )}>
                      {getStatusText(duplicationStatus.configStatus)}
                    </span>
                  </div>
                </div>

                {/* Data Sets Status */}
                {duplicationStatus.datasets.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Duplicate Data Sets
                    </p>
                    <ul className="space-y-2.5">
                      {duplicationStatus.datasets.map((dataset, index) => {
                        const isFailed =
                          dataset.status === DuplicationItemStatus.ItemFailed;
                        return (
                          <li
                            key={index}
                            className={classNames(
                              "flex items-center gap-3 pl-4 border-l-2",
                              isFailed ? "border-red-500" : "border-gray-200"
                            )}>
                            <span
                              className={classNames(
                                "text-sm",
                                isFailed
                                  ? "text-red-700 font-medium"
                                  : "text-gray-600"
                              )}>
                              {dataset.name}
                            </span>
                            <div className="flex items-center gap-2">
                              {dataset.status ===
                                DuplicationItemStatus.ItemOk && (
                                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                              )}
                              {dataset.status ===
                                DuplicationItemStatus.ItemInProgress && (
                                <ClockIcon className="h-5 w-5 text-blue-600" />
                              )}
                              {dataset.status ===
                                DuplicationItemStatus.ItemPending && (
                                <ClockIcon className="h-5 w-5 text-gray-500" />
                              )}
                              {dataset.status ===
                                DuplicationItemStatus.ItemFailed && (
                                <XCircleIcon className="h-5 w-5 text-red-600" />
                              )}
                              <span
                                className={classNames(
                                  "text-sm font-medium",
                                  getStatusColorClass(dataset.status)
                                )}>
                                {getStatusText(dataset.status)}
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Reports Status */}
                {duplicationStatus.reportsStatus && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">
                      Duplicate Reports
                    </span>
                    <div className="flex items-center gap-2">
                      {duplicationStatus.reportsStatus ===
                        DuplicationItemStatus.ItemOk && (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      )}
                      {duplicationStatus.reportsStatus ===
                        DuplicationItemStatus.ItemInProgress && (
                        <ClockIcon className="h-5 w-5 text-blue-600" />
                      )}
                      {duplicationStatus.reportsStatus ===
                        DuplicationItemStatus.ItemPending && (
                        <ClockIcon className="h-5 w-5 text-gray-500" />
                      )}
                      {duplicationStatus.reportsStatus ===
                        DuplicationItemStatus.ItemFailed && (
                        <XCircleIcon className="h-5 w-5 text-red-600" />
                      )}
                      <span
                        className={classNames(
                          "text-sm font-medium",
                          getStatusColorClass(duplicationStatus.reportsStatus)
                        )}>
                        {getStatusText(duplicationStatus.reportsStatus)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Error Messages Section */}
                {hasErrors && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <h4 className="text-sm font-semibold text-red-800 mb-2">
                        Errors occurred during duplication:
                      </h4>
                      <ul className="space-y-2 text-sm text-red-700">
                        {duplicationStatus?.errorMessage && (
                          <li>• {duplicationStatus.errorMessage}</li>
                        )}
                        {duplicationStatus?.reportsErrorMessage && (
                          <li>
                            • Reports: {duplicationStatus.reportsErrorMessage}
                          </li>
                        )}
                        {duplicationStatus?.datasets
                          ?.filter(ds => ds.errorMessage)
                          .map((ds, idx) => (
                            <li key={idx}>
                              • {ds.name}: {ds.errorMessage}
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                )}

                {duplicationStatus?.status === DuplicationStatus.InProgress && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleCancelDuplication}
                      disabled={isCancellingDuplication}
                      className="w-full px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {isCancellingDuplication
                        ? "Cancelling..."
                        : "Cancel Duplication"}
                    </button>
                  </div>
                )}

                {/* Go to Duplicated Run Button - Show when completed and no errors */}
                {isDuplicationCompleted && !hasErrors && duplicatedRunId && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleGoToDuplicatedRun}
                      className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">
                      Go to Duplicated Run
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          {mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN && (
            <div className="my-4 mx-0 max-w-[550px]">
              <p className="text-sm leading-6 text-slate-600 my-4">
                Select a target project to duplicate your run.
              </p>

              {/* Change Pipeline Version Toggle - only show if there are pipeline revisions */}
              {pipelineRevisions.length > 0 && (
                <label className="flex items-center gap-2 mb-4">
                  <FormInputComponent
                    control={control}
                    name="changePipelineVersion"
                    type="checkbox"
                    className="h-6 w-6 -mt-2"
                  />
                  <div className="whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
                    Change Pipeline Version
                  </div>
                </label>
              )}

              {/* Pipeline Revision Selection - only show when toggle is on and revisions exist */}
              {pipelineRevisions.length > 0 && changePipelineVersion && (
                <>
                  <label className="mb-2 block text-sm font-medium leading-6">
                    Select Pipeline Revision
                  </label>
                  <SelectComponent
                    name="pipelineRevisions"
                    enableClear={false}
                    options={pipelineRevisions}
                    isLoading={isPipelineRevisionHistoryLoading}
                    value={selectedRevision}
                    onChange={handlePipelineRevisionChange}
                    formatOptionLabel={(option, { context }) => {
                      return context === "menu" ? (
                        <div className="flex flex-col items-start">
                          <span>
                            {option?.version} on{" "}
                            {formatDateString(option.createdAt ?? "", "LL")}
                          </span>
                          <span className="line-clamp-2 text-xs text-gray-400">
                            {option?.revComment}
                          </span>
                        </div>
                      ) : (
                        option.label
                      );
                    }}
                  />
                </>
              )}
            </div>
          )}
          <form onSubmit={e => e.preventDefault()}>
            <div className="my-5">
              <label className="mb-2 block text-sm font-medium leading-6">
                Choose Project for Run<span className="text-red-400"> *</span>
              </label>
              <ProjectsFilter
                hideInputFilters
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                setPage={setProjectsPage}
                setPageSize={() => {}}
              />
              <div className="mb-4">
                <SelectComponent
                  control={control}
                  name="projectId"
                  rules={requiredValidation("Project")}
                  options={projectListOptions}
                  isLoading={
                    isProjectsLoading ||
                    isCreateProjectLoading ||
                    isMissingProjectLoading
                  }
                  placeholder={"Choose or Create a Project"}
                  prefixIcon={<BeakerIcon />}
                  isCreatable={true}
                  onCreateOption={handleCreateNewProject}
                  onFetchMoreOptions={fetchMoreProjects}
                  onInputChange={inputValue =>
                    debouncedHandleInputChange(inputValue)
                  }
                  formatOptionLabel={(option, { context }) =>
                    context === "menu" ? (
                      <div className="flex flex-col gap-1">
                        {/* Project Name */}
                        <span className="font-semibold text-black">
                          {option.metadata?.name || option.label}
                        </span>

                        {option.metadata && (
                          <div className="text-sm text-gray-600">
                            {/* Owner */}
                            <div className="flex items-center gap-1">
                              <UserIcon className="h-4 w-4 text-indigo-600" />
                              <span className="font-medium">Owner:</span>
                              <span>{option.metadata.owner}</span>
                            </div>

                            {/* Last Updated Date */}
                            <div className="flex items-center gap-1">
                              <CalendarDaysIcon className="h-4 w-4 text-red-600" />
                              <span className="font-medium">Last updated:</span>
                              <span>
                                {formatDateString(
                                  option.metadata.updatedAt,
                                  "LLL"
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span>{option.label}</span>
                    )
                  }
                />
                {errors?.projectId && (
                  <span className="text-xs text-rose-500">
                    {errors.projectId.message}
                  </span>
                )}
              </div>

              {mode === RUN_MANAGEMENT_MODALS.DUPLICATE_RUN &&
                (pipelineRevisions.length === 0 || !changePipelineVersion) && (
                  <div className="mt-6">
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <button
                        type="button"
                        onClick={() => setShowAdditionalOptions(prev => !prev)}
                        aria-expanded={showAdditionalOptions}
                        className="flex w-full items-center justify-between px-4 py-3 text-left">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          <ChevronRightIcon
                            className={classNames(
                              "h-4 w-4 text-gray-600 transition-transform",
                              showAdditionalOptions ? "rotate-90" : ""
                            )}
                          />
                          Include Run Assets
                        </div>
                        <span className="text-xs font-medium uppercase text-gray-400">
                          Optional
                        </span>
                      </button>
                      {showAdditionalOptions && (
                        <div className="space-y-4 border-t border-gray-200 px-4 py-4">
                          {/* Include Logs */}
                          <label className="flex items-center gap-2">
                            <FormInputComponent
                              control={control}
                              name="include.logs"
                              type="checkbox"
                              className="h-6 w-6 -mt-2"
                            />
                            <div className="whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
                              Include Logs
                            </div>
                          </label>

                          {/* Include Reports (toggle) */}
                          <label className="flex items-center gap-2">
                            <FormInputComponent
                              control={control}
                              name="includeReportsToggle"
                              type="checkbox"
                              className={classNames(
                                "h-6 w-6 -mt-2",
                                !canDuplicateReports || isDuplicateCheckLoading
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              )}
                              disabled={
                                !canDuplicateReports || isDuplicateCheckLoading
                              }
                            />
                            <div className="whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
                              Include Reports
                            </div>
                            {!canDuplicateReports &&
                              !isDuplicateCheckLoading && (
                                <span className="text-xs text-amber-600 ml-2">
                                  (Only GCS to GCS supported)
                                </span>
                              )}
                          </label>

                          {/* Include Data Sets (toggle) */}
                          <label className="flex items-center gap-2">
                            <FormInputComponent
                              control={control}
                              name="includeDataSetsToggle"
                              type="checkbox"
                              className={classNames(
                                "h-6 w-6 -mt-2",
                                !canDuplicateDatasets || isDuplicateCheckLoading
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              )}
                              disabled={
                                !canDuplicateDatasets || isDuplicateCheckLoading
                              }
                            />
                            <div className="whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
                              Include Data Sets
                            </div>
                            {!canDuplicateDatasets &&
                              !isDuplicateCheckLoading && (
                                <span className="text-xs text-amber-600 ml-2">
                                  (Only GCS to GCS supported)
                                </span>
                              )}
                          </label>

                          {/* Shared destination/path selection (conditional) */}
                          {showDestinationSelection && (
                            <div>
                              <label className="mb-2 block text-sm font-medium leading-6">
                                Select a Destination
                                <span className="text-red-400"> *</span>
                              </label>
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <SelectComponent
                                    control={control}
                                    name="include.reports.dataSourceId"
                                    placeholder="Select a destination"
                                    options={dataSourceOptions}
                                    isLoading={isDataSourceListLoading}
                                    rules={requiredValidation("Destination")}
                                    onChange={opt => {
                                      const found = dataSourceOptions.find(
                                        o =>
                                          o.value ===
                                          (opt as SelectOption)?.value
                                      );
                                      if (found?.metadata?.path) {
                                        setValue(
                                          "include.reports.path",
                                          normalizeProviderPath(
                                            found.metadata.provider,
                                            found.metadata.path
                                          ),
                                          {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                          }
                                        );
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              {(errors as FieldErrors<DuplicateRunFormValues>)
                                ?.include?.reports?.dataSourceId && (
                                <span className="text-xs text-rose-500">
                                  {
                                    (
                                      errors as FieldErrors<DuplicateRunFormValues>
                                    ).include!.reports!.dataSourceId!
                                      .message as any
                                  }
                                </span>
                              )}

                              <FormInputComponent
                                control={control}
                                name="include.reports.path"
                                label="Path"
                                placeholder="Path"
                                validations={requiredValidation("Path")}
                                showRequiredField
                              />

                              {/* Final Destination Preview */}
                              {finalPaths && selectedPath && (
                                <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                                  <p className="mb-2 text-xs font-medium text-gray-700">
                                    Final destinations:
                                  </p>
                                  <div className="space-y-1.5">
                                    {includeReportsToggle && (
                                      <div className="flex items-start gap-2">
                                        <span className="text-xs text-gray-500 mt-0.5">
                                          •
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          <span className="text-xs font-medium text-gray-600">
                                            Reports:
                                          </span>
                                          <p className="text-xs text-gray-700 font-mono break-all mt-0.5">
                                            {finalPaths.reports}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    {includeDataSetsToggle && (
                                      <div className="flex items-start gap-2">
                                        <span className="text-xs text-gray-500 mt-0.5">
                                          •
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          <span className="text-xs font-medium text-gray-600">
                                            Data Sets:
                                          </span>
                                          <p className="text-xs text-gray-700 font-mono break-all mt-0.5">
                                            {finalPaths.datasets}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Permissions Section */}
              <RunPermissionsSection watch={watch} setValue={setValue} />
            </div>
          </form>
        </>
      )}
      <div className="mx-4 my-2"></div>
      {isDuplicateRunLoading ||
        (isChangeProjectModalLoading && <OverlayLoading />)}

      {isRunDuplicationToBeCanceled && (
        <DeleteModal
          item={isRunDuplicationToBeCanceled}
          setItem={setIsRunDuplicationToBeCanceled}
          deleteFunction={async () => cancelDuplication({ runId })}
          isLoading={isCancellingDuplication}
          title="Cancel Duplication"
          message={() => (
            <div className="space-y-4 text-sm text-gray-700">
              <p>
                Are you sure you want to cancel this duplication? All ongoing
                transfers will be stopped.
              </p>
            </div>
          )}
          confirmText={"Cancel Duplication"}
        />
      )}
    </Modal>
  );
};

export default ChangeProjectDuplicateRunModal;
